import moment from "moment";

// serializer
import DoctorNoteTemplateGroupSerializer from "issara-sdk/types/DoctorNoteTemplateGroupSerializer_apps_APP";
import PatientAppointmentSerializer from "issara-sdk/types/PatientAppointmentSerializer_apps_QUE";

import { getDayOfWeekThaiLong } from "react-lib/apps/MEH/appointments/AppointmentMeHeyUtils";
import { formatDate } from "react-lib/utils/dateUtils";

import type { SetEntry } from "../common/sequence/OrderSheetV4";
import type { PickItem } from "./ordersModelV4";
import type { ChipV4Tone } from "../common/ui";
import type { Block } from "../appointment/appointmentModelV4";

// ---------- รายการนัด ----------

export type AppointmentKind = "general" | "surgery";

export type AppointmentRow = {
  id: number;
  kind: AppointmentKind;
  /** YYYY-MM-DD HH:mm สำหรับเรียง */
  sortKey: string;
  dateText: string;
  time: string;
  duration: number;
  provider: string;
  division: string;
  purpose: string;
  typeText: string;
  orders: AdvanceOrder[];
  isCancelled: boolean;
  statusText: string;
  raw: PatientAppointmentSerializer;
};

export const APPOINTMENT_KINDS: { kind: AppointmentKind; text: string; tone: ChipV4Tone }[] = [
  { kind: "general", text: "นัดหมายทั่วไป", tone: "orange" },
  { kind: "surgery", text: "นัดหมายผ่าตัด (EN ใหม่)", tone: "teal" },
];

export const CANCELLED_STATUS = [5, 6];

/** ยกเลิกนัดโดย: status ของ patient-appointment (5 ผู้ป่วยยกเลิก, 6 ผู้ให้บริการยกเลิก) */
export const CANCEL_REASONS: { text: string; status: number }[] = [
  { text: "แพทย์ยกเลิก", status: 6 },
  { text: "ผู้ป่วยยกเลิก", status: 5 },
  { text: "คลินิกยกเลิก (อื่นๆ)", status: 6 },
];

/** "ศุกร์ 30/10/2569" */
export const dayDateText = (date: moment.Moment): string =>
  `${getDayOfWeekThaiLong(date.day())} ${formatDate(date)}`;

export const kindOf = (kind: string): (typeof APPOINTMENT_KINDS)[number] =>
  APPOINTMENT_KINDS.find((item) => item.kind === kind) ?? APPOINTMENT_KINDS[0];

export const toAppointmentRow = (
  item: PatientAppointmentSerializer,
  typeOptions: { value: string | number; text: string }[],
): AppointmentRow => {
  const at = moment(item.estimated_at_iso);
  const isCancelled = CANCELLED_STATUS.includes(Number(item.status));

  return {
    id: Number(item.id),
    kind: kindOf(item.extra?.kind).kind,
    sortKey: at.format("YYYY-MM-DD HH:mm"),
    dateText: at.isValid() ? dayDateText(at) : "",
    time: at.isValid() ? at.format("HH:mm") : "",
    duration: Number(item.estimated_duration) || 0,
    provider: item.display_info?.provider_name ?? item.doctor_full_name ?? "",
    division: item.division_name ?? "",
    purpose: item.extra?.appointmentFor ?? "",
    typeText:
      item.type_display ??
      typeOptions.find((option) => String(option.value) === String(item.type))?.text ??
      "",
    orders: item.extra?.orders ?? [],
    isCancelled,
    statusText: isCancelled ? `ยกเลิกนัด (${item.status_note || "-"})` : "นัดแล้ว",
    raw: item,
  };
};

export const sortRows = (rows: AppointmentRow[]): AppointmentRow[] =>
  [...rows].sort((a, b) => a.sortKey.localeCompare(b.sortKey));

/** นัดที่ถึงวันนี้และมีคำสั่งล่วงหน้าที่ยังไม่ดึงเข้า Encounter */
export const isDueToday = (item: PatientAppointmentSerializer): boolean =>
  !CANCELLED_STATUS.includes(Number(item.status)) &&
  moment(item.estimated_at_iso).isSame(moment(), "day") &&
  (item.extra?.orders ?? []).length > 0 &&
  !item.extra?.pulledEncounter;

// ---------- ฟอร์มนัด ----------

export type AppointmentDraft = {
  id?: number;
  kind: AppointmentKind;
  block: Block | null;
  time: string;
  duration: string;
  type: string;
  appointmentFor: string;
  advice: string;
  orders: AdvanceOrder[];
};

export const TIME_STEP = 15;

export const emptyDraft = (kind: AppointmentKind): AppointmentDraft => ({
  kind,
  block: null,
  time: "",
  duration: "",
  type: "",
  appointmentFor: "",
  advice: "",
  orders: [],
});

/** ช่องที่ยังไม่ครบหรือไม่ถูกต้อง */
export const draftProblems = (draft: AppointmentDraft): string[] => {
  const block = draft.block;
  const duration = Number(draft.duration);
  const end = block ? moment(block.end, "HH:mm") : null;
  const startAt = moment(draft.time, "HH:mm", true);

  return [
    block ? "" : "วันเวลาออกตรวจ",
    startAt.isValid() ? "" : "เวลานัดหมาย",
    block &&
    startAt.isValid() &&
    (startAt.isBefore(moment(block.start, "HH:mm")) || !startAt.isBefore(end))
      ? "เวลานัดหมายต้องอยู่ในช่วงคาบ"
      : "",
    duration > 0 ? "" : "ระยะเวลา (นาที)",
    draft.type ? "" : "ประเภทการนัดหมาย",
    draft.appointmentFor ? "" : "นัดเพื่อ",
  ].filter(Boolean);
};

/** เวลาเริ่มนัดทุก 15 นาทีในคาบ (เริ่มได้ถึงก่อนหมดคาบ) */
export const timeOptions = (start: string, end: string): string[] => {
  const from = moment(start, "HH:mm");
  const to = moment(end, "HH:mm");
  const times: string[] = [];

  for (const at = from.clone(); at.isBefore(to); at.add(TIME_STEP, "minutes")) {
    times.push(at.format("HH:mm"));
  }

  return times;
};

// ---------- ปฏิทิน ----------

export type ViewType = "week" | "month" | "day";

/** ช่องวันของปฏิทิน เริ่มวันจันทร์ */
export const cellsOf = (view: ViewType, anchor: moment.Moment): Date[] => {
  const start =
    view === "month"
      ? anchor.clone().startOf("month").isoWeekday(1)
      : view === "week"
        ? anchor.clone().isoWeekday(1)
        : anchor.clone();
  const count =
    view === "month"
      ? Math.ceil((anchor.clone().endOf("month").diff(start, "days") + 1) / 7) * 7
      : view === "week"
        ? 7
        : 1;

  return Array.from({ length: count }, (_, index) => start.clone().add(index, "days").toDate());
};

export const rangeText = (view: ViewType, anchor: moment.Moment, cells: Date[]): string => {
  if (view === "month") {
    return anchor.clone().locale("th").format("MMMM YYYY");
  }

  if (view === "week") {
    const [first, last] = [moment(cells[0]).locale("th"), moment(cells[6]).locale("th")];

    return `${first.format("D MMM")} – ${last.format("D MMM YYYY")}`;
  }

  return anchor.clone().locale("th").format("dddd D MMMM YYYY");
};

// ---------- คำแนะนำ ----------

export type AdviceItem = { key: string; text: string; isCustom: boolean };

export type AdviceGroup = { key: string; name: string; texts: string[]; isCustom: boolean };

/** ต่อท้ายคำแนะนำ บรรทัดละ "- ข้อความ" ไม่ซ้ำกันในครั้งเดียว */
export const appendAdvice = (current: string, texts: string[]): string => {
  const lines = [...new Set(texts.map((text) => text.trim()).filter(Boolean))].map(
    (text) => `- ${text}`,
  );

  return [current.trimEnd(), ...lines].filter(Boolean).join("\n");
};

/** ข้อความที่จะเพิ่มจากรายข้อและชุดที่เลือก (ไม่ซ้ำ) */
export const pickedAdviceTexts = (
  itemTexts: string[],
  groups: DoctorNoteTemplateGroupSerializer[],
): string[] => [
  ...new Set([
    ...itemTexts,
    ...groups.flatMap((group) => (group.note_templates ?? []).map((note) => note.text)),
  ]),
];

export const toAdviceGroup = (group: DoctorNoteTemplateGroupSerializer): AdviceGroup => ({
  key: `g${group.id}`,
  name: group.name,
  texts: (group.note_templates ?? []).map((note) => note.text),
  isCustom: false,
});

export const toggle = (list: string[], key: string): string[] =>
  list.includes(key) ? list.filter((value) => value !== key) : [...list, key];

// ---------- คำสั่งล่วงหน้า ----------

/** คำสั่งล่วงหน้า 1 รายการ (เก็บใน extra.orders ของนัด) */
export type AdvanceOrder = SetEntry & { note: string };

export const advanceTotal = (orders: AdvanceOrder[]): number =>
  orders.reduce((sum, order) => sum + (Number(order.price) || 0), 0);

export const advanceId = (index: number): string => `P-${String(index + 1).padStart(3, "0")}`;

export const toEntry = (pick: PickItem): SetEntry => ({
  key: pick.key,
  kind: pick.category,
  name: pick.name,
  price: Number(pick.price || 0),
  quantity: 1,
  item: pick.item,
  fields: pick.fields,
  lab: pick.lab,
});
